/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { CommonModule } from "@angular/common";
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  ElementRef,
  inject,
  Input,
  OnDestroy,
  OnInit,
  signal,
} from "@angular/core";
import { FormControl, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { debounceTime, distinctUntilChanged, Subject, takeUntil } from "rxjs";

// PrimeNG
import { ConfirmationService, MessageService } from "primeng/api";
import { ButtonModule } from "primeng/button";
import { ConfirmDialogModule } from "primeng/confirmdialog";
import { DialogService, DynamicDialogModule } from "primeng/dynamicdialog";
import { ToastModule } from "primeng/toast";
import { TooltipModule } from "primeng/tooltip";

// Core Services
import {
  QPilotAccessToken,
  QPilotUser,
} from "../../../../core/services/qpilot.service";
import { SessionService } from "../../../../core/services/session.service";

// Feature Services & Interfaces
import { QPilotCoupon } from "../../../../core/models/qpilot/coupon.interface";
import { COUPON_MESSAGES } from "../../constants/coupon.constants";
import {
  CouponFilter,
  CouponFilterControls,
  CouponListResponse,
  CouponPageEvent,
  CouponRowActionEvent,
  CouponSortEvent,
  DEFAULT_COUPON_FILTER,
} from "../../interfaces";
import { CouponsService } from "../../services/coupons.service";

// Child Components
import { CouponsFiltersComponent } from "../../components/coupons-filters/coupons-filters.component";
import { ListPageSkeletonComponent } from "../../../../shared/components/table-skeleton";
import { CouponsTableComponent } from "../../components/coupons-table/coupons-table.component";
import { ImportDialogComponent } from "../../components/import-dialog/import-dialog.component";

/**
 * Coupons List Page Component
 * Main container for the coupons list with filters, table, and actions
 * Based on scheduled-orders-list pattern
 */
@Component({
  selector: "app-coupons-list",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ToastModule,
    ConfirmDialogModule,
    ButtonModule,
    TooltipModule,
    DynamicDialogModule,
    CouponsFiltersComponent,
    CouponsTableComponent,
    ListPageSkeletonComponent,
  ],
  templateUrl: "./coupons-list.component.html",
  styleUrl: "./coupons-list.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ConfirmationService, DialogService],
  // Note: MessageService is NOT provided locally - we use the root instance
  // This ensures both component toast calls AND API interceptor error toasts
  // go to the same MessageService that the <p-toast> listens to
})
export class CouponsListComponent implements OnInit, OnDestroy {
  // Embedded mode inputs (for web component/iframe usage)
  @Input() inputSiteId?: number;
  @Input() inputToken?: QPilotAccessToken;
  @Input() embeddedMode = false;
  @Input() hideToolbar = false;

  // Services
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly elementRef = inject(ElementRef);
  private readonly sessionService = inject(SessionService);
  private readonly couponsService = inject(CouponsService);
  private readonly messageService = inject(MessageService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly dialogService = inject(DialogService);

  // Cleanup
  private readonly destroy$ = new Subject<void>();

  // UI State signals
  readonly sidebarCollapsed = signal(false);
  private userManuallyCollapsed = false;
  private readonly isLoading = signal(false);
  private readonly isInitialLoading = signal(true);

  // Data signals
  private readonly couponsData = signal<CouponListResponse | null>(null);
  private readonly currentSiteIdSignal = signal<number | null>(null);

  // Filter state signal (for reactive hasActiveFilters)
  private readonly searchFilterValue = signal<string>("");

  // Filter form - strongly typed
  filterForm: FormGroup<CouponFilterControls>;

  // Computed values for template
  readonly loading = computed(() => this.isLoading());
  readonly initialLoading = computed(() => this.isInitialLoading());
  readonly data = computed(() => this.couponsData());
  readonly currentSiteId = computed(() => this.currentSiteIdSignal());

  // Check if any filters are active (beyond defaults)
  readonly hasActiveFilters = computed(() => {
    const search = this.searchFilterValue();
    return !!(search && search.trim().length > 0);
  });

  constructor() {
    // Initialize filter form with typed controls
    this.filterForm = new FormGroup<CouponFilterControls>({
      search: new FormControl("", { nonNullable: true }),
      discountType: new FormControl("", { nonNullable: true }),
      page: new FormControl(DEFAULT_COUPON_FILTER.page, { nonNullable: true }),
      pageSize: new FormControl(DEFAULT_COUPON_FILTER.pageSize, {
        nonNullable: true,
      }),
      orderBy: new FormControl(DEFAULT_COUPON_FILTER.orderBy, {
        nonNullable: true,
      }),
      order: new FormControl<"asc" | "desc">(DEFAULT_COUPON_FILTER.order, {
        nonNullable: true,
      }),
    });
  }

  ngOnInit(): void {
    this.detectEmbeddedMode();
    this.initializeSiteId();
    this.initializeResponsiveSidebar();
    this.setupWindowResizeListener();
    this.setupFilterSubscriptions();
  }

  /**
   * Detect embedded mode from HTML attributes (for web component mode)
   * This is CRITICAL for web components to work properly
   */
  private detectEmbeddedMode(): void {
    const element = this.elementRef.nativeElement as HTMLElement;

    // Check HTML attribute for embedded mode
    const embeddedModeAttr =
      element.getAttribute("embedded-mode") ??
      element.getAttribute("embeddedMode");

    if (embeddedModeAttr === "true" || embeddedModeAttr === "") {
      this.embeddedMode = true;
    }

    // Get siteId from attribute if in embedded mode
    if (this.embeddedMode) {
      const siteIdString =
        this.inputSiteId?.toString() ??
        element.getAttribute("input-site-id") ??
        element.getAttribute("inputSiteId");

      if (siteIdString) {
        this.currentSiteIdSignal.set(parseInt(siteIdString, 10));
      }

      // Get token from attribute and convert
      const tokenString =
        element.getAttribute("input-token") ??
        element.getAttribute("inputToken");

      // Create inputToken from attribute if:
      // 1. We have a token string from attribute
      // 2. AND inputToken is either missing, not an object, or doesn't have valid TokenBearerAuth
      // Note: Angular Elements may bind the attribute as a raw string, so we check for proper structure
      if (
        tokenString &&
        (!this.inputToken ||
          typeof this.inputToken !== "object" ||
          !this.inputToken.TokenBearerAuth)
      ) {
        this.inputToken = {
          TokenBearerAuth: tokenString,
          TokenAuth: "",
          Id: 0,
          UserId: 0,
          CreatedUtc: new Date(),
          UpdatedUtc: new Date(),
          ExpiresUtc: new Date(),
          User: {} as QPilotUser,
          FirstLogin: 0,
        };
      }
    }
  }

  /**
   * Get access token based on mode (embedded vs standard)
   */
  private getAccessToken(): QPilotAccessToken | undefined {
    if (this.embeddedMode && this.inputToken) {
      return this.inputToken;
    }
    return this.sessionService.accessToken ?? undefined;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Initialize site ID from route or input
   */
  private initializeSiteId(): void {
    // Embedded mode: siteId already set in detectEmbeddedMode
    if (this.embeddedMode && this.inputToken && this.currentSiteIdSignal()) {
      this.loadCoupons();
      return;
    }

    // Standard mode: get site ID from route
    if (!this.embeddedMode) {
      this.route.parent?.paramMap
        .pipe(takeUntil(this.destroy$))
        .subscribe((params) => {
          const siteIdParam = params.get("siteId");
          const siteId = siteIdParam ? parseInt(siteIdParam, 10) : null;

          if (siteId && !isNaN(siteId)) {
            this.currentSiteIdSignal.set(siteId);
            this.loadCoupons();
          }
        });
    }
  }

  /**
   * Setup reactive filter subscriptions
   */
  private setupFilterSubscriptions(): void {
    // Debounce search input
    this.filterForm.controls.search.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((value) => {
        this.searchFilterValue.set(value);
        this.filterForm.patchValue({ page: 1 });
        this.loadCoupons();
      });

    // Immediate response for discount type filter
    this.filterForm.controls.discountType.valueChanges
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => {
        this.filterForm.patchValue({ page: 1 });
        this.loadCoupons();
      });
  }

  /**
   * Load coupons from API
   */
  private loadCoupons(): void {
    const siteId = this.currentSiteIdSignal();
    if (!siteId) return;

    this.isLoading.set(true);

    const filter: CouponFilter = {
      search: this.filterForm.value.search ?? "",
      discountType: this.filterForm.value.discountType ?? "",
      page: this.filterForm.value.page ?? DEFAULT_COUPON_FILTER.page,
      pageSize:
        this.filterForm.value.pageSize ?? DEFAULT_COUPON_FILTER.pageSize,
      orderBy: this.filterForm.value.orderBy ?? DEFAULT_COUPON_FILTER.orderBy,
      order: this.filterForm.value.order ?? DEFAULT_COUPON_FILTER.order,
    };

    const accessToken = this.getAccessToken();
    this.couponsService
      .getCoupons(siteId, filter, accessToken)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.couponsData.set(response);
          this.isLoading.set(false);
          this.isInitialLoading.set(false);
        },
        error: (error) => {
          console.error("Failed to load coupons:", error);
          this.messageService.add({
            severity: "error",
            summary: "Error",
            detail: COUPON_MESSAGES.LOAD_ERROR,
          });
          this.isLoading.set(false);
          this.isInitialLoading.set(false);
        },
      });
  }

  // ==================== UI Event Handlers ====================

  /**
   * Toggle sidebar collapsed state
   */
  toggleSidebar(): void {
    const newState = !this.sidebarCollapsed();
    this.sidebarCollapsed.set(newState);
    this.userManuallyCollapsed = newState;
  }

  /**
   * Initialize sidebar state based on screen size
   */
  private initializeResponsiveSidebar(): void {
    const isMobile = window.innerWidth <= 768;
    this.sidebarCollapsed.set(isMobile);
  }

  /**
   * Setup window resize listener for responsive behavior
   */
  private setupWindowResizeListener(): void {
    const resizeObserver = new ResizeObserver(() => {
      const isMobile = window.innerWidth <= 768;

      if (isMobile && !this.userManuallyCollapsed) {
        this.sidebarCollapsed.set(true);
      } else if (!isMobile && !this.userManuallyCollapsed) {
        this.sidebarCollapsed.set(false);
      }

      this.cdr.markForCheck();
    });

    resizeObserver.observe(document.body);

    this.destroy$.subscribe(() => {
      resizeObserver.disconnect();
    });
  }

  /**
   * Handle filter changes from filters component
   */
  onFiltersChanged(filters: Partial<CouponFilter>): void {
    this.filterForm.patchValue({ ...filters, page: 1 });
    this.loadCoupons();
  }

  /**
   * Clear all filters and reset to defaults
   */
  onClearFilters(): void {
    this.searchFilterValue.set("");
    this.filterForm.reset(DEFAULT_COUPON_FILTER);
    this.loadCoupons();
  }

  /**
   * Handle sort changes from table
   */
  onSortChanged(event: CouponSortEvent): void {
    this.filterForm.patchValue({
      orderBy: event.field,
      order: event.order === 1 ? "asc" : "desc",
      page: 1,
    });
    this.loadCoupons();
  }

  /**
   * Handle page changes from table
   */
  onPageChanged(event: CouponPageEvent): void {
    const page = event.page !== undefined ? event.page + 1 : 1;
    this.filterForm.patchValue({
      page,
      pageSize: event.rows,
    });
    this.loadCoupons();
  }

  /**
   * Handle row actions (edit, delete)
   */
  onRowAction(event: CouponRowActionEvent): void {
    switch (event.action) {
      case "edit":
        this.navigateToEdit(event.coupon);
        break;
      case "delete":
        this.confirmDelete(event.coupon);
        break;
    }
  }

  /**
   * Navigate to create new coupon
   */
  onCreateNew(): void {
    if (this.embeddedMode) {
      // Emit event for embedded mode with standard qmc-navigate pattern
      const event = new CustomEvent("qmc-navigate", {
        detail: {
          component: "coupons-list",
          action: "create",
          siteId: this.currentSiteIdSignal(),
        },
        bubbles: true,
        composed: true,
      });
      this.elementRef.nativeElement.dispatchEvent(event);
    } else {
      void this.router.navigate(["create"], { relativeTo: this.route });
    }
  }

  /**
   * Navigate to edit coupon
   */
  private navigateToEdit(coupon: QPilotCoupon): void {
    if (this.embeddedMode) {
      // Emit event for embedded mode with standard qmc-navigate pattern
      const event = new CustomEvent("qmc-navigate", {
        detail: {
          component: "coupons-list",
          action: "edit",
          couponId: coupon.Id,
          siteId: this.currentSiteIdSignal(),
        },
        bubbles: true,
        composed: true,
      });
      this.elementRef.nativeElement.dispatchEvent(event);
    } else {
      void this.router.navigate([coupon.Id, "edit"], {
        relativeTo: this.route,
      });
    }
  }

  /**
   * Show delete confirmation dialog
   * Includes coupon code in message (matches old QMC behavior)
   */
  private confirmDelete(coupon: QPilotCoupon): void {
    this.confirmationService.confirm({
      message: `Are you sure you want to delete coupon <strong>${coupon.Code}</strong>?`,
      header: COUPON_MESSAGES.DELETE_CONFIRM_TITLE,
      icon: "pi pi-exclamation-triangle",
      acceptLabel: COUPON_MESSAGES.DELETE_CONFIRM_ACCEPT,
      rejectLabel: COUPON_MESSAGES.DELETE_CONFIRM_REJECT,
      acceptButtonStyleClass: "p-button-danger",
      accept: () => this.deleteCoupon(coupon),
    });
  }

  /**
   * Delete coupon
   */
  private deleteCoupon(coupon: QPilotCoupon): void {
    this.isLoading.set(true);

    const accessToken = this.getAccessToken();
    this.couponsService
      .deleteCoupon(coupon.Id, accessToken)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: "success",
            summary: "Success",
            detail: COUPON_MESSAGES.DELETE_SUCCESS,
          });
          this.loadCoupons();
        },
        error: (error) => {
          console.error("Failed to delete coupon:", error);
          this.messageService.add({
            severity: "error",
            summary: "Error",
            detail: COUPON_MESSAGES.DELETE_ERROR,
          });
          this.isLoading.set(false);
        },
      });
  }

  /**
   * Export coupons to CSV
   */
  onExport(): void {
    const siteId = this.currentSiteIdSignal();
    if (!siteId) return;

    this.isLoading.set(true);

    const accessToken = this.getAccessToken();
    this.couponsService
      .exportCoupons(siteId, accessToken)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (csvContent) => {
          // Create and download file with UTF-8 BOM for Excel compatibility
          const BOM = "\uFEFF";
          const blob = new Blob([BOM + csvContent], {
            type: "text/csv;charset=utf-8",
          });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          // Use full timestamp format matching old QMC: Coupons_Export_2025-01-05T14-30-00.csv
          const timestamp = new Date()
            .toISOString()
            .substring(0, 19)
            .replace(/:/g, "-");
          link.download = `Coupons_Export_${timestamp}.csv`;
          link.click();
          window.URL.revokeObjectURL(url);

          this.messageService.add({
            severity: "success",
            summary: "Success",
            detail: COUPON_MESSAGES.EXPORT_SUCCESS,
          });
          this.isLoading.set(false);
        },
        error: (error) => {
          console.error("Failed to export coupons:", error);
          this.messageService.add({
            severity: "error",
            summary: "Error",
            detail: COUPON_MESSAGES.EXPORT_ERROR,
          });
          this.isLoading.set(false);
        },
      });
  }

  /**
   * Open import dialog
   */
  onImport(): void {
    const siteId = this.currentSiteIdSignal();
    if (!siteId) return;

    const ref = this.dialogService.open(ImportDialogComponent, {
      header: "Import Coupons",
      width: "600px",
      modal: true,
      dismissableMask: true,
      data: {
        siteId,
        embeddedMode: this.embeddedMode,
        inputToken: this.inputToken,
      },
      styleClass: "import-dialog-container",
    });

    ref.onClose.pipe(takeUntil(this.destroy$)).subscribe((result) => {
      if (result?.success) {
        // Reload coupons after successful import
        this.loadCoupons();
      }
    });
  }
}
